import { Calendar, ClipboardCheck, BarChart3, Trophy, CalendarDays, BookOpen, Shield, CreditCard } from 'lucide-react'

const iconMap = {
  calendar: Calendar,
  clipboard: ClipboardCheck,
  'bar-chart': BarChart3,
  trophy: Trophy,
  shield: Shield,
  'calendar-days': CalendarDays,
  book: BookOpen,
  'credit-card': CreditCard,
}

export type IconType = keyof typeof iconMap

export interface NavigationItem {
  label: string
  shortLabel?: string
  href: string
  icon: IconType
  iconUrl?: string
  isActive?: boolean
}

interface MainNavProps {
  items: NavigationItem[]
  onNavigate?: (href: string) => void
  variant: 'top' | 'bottom'
}

export function MainNav({ items, onNavigate, variant }: MainNavProps) {
  if (variant === 'bottom') {
    return (
      <nav className="bg-white dark:bg-zinc-900 border-t border-zinc-200 dark:border-zinc-800 pb-[env(safe-area-inset-bottom)]">
        <div className="flex items-center justify-around h-14">
          {items.map((item) => {
            const Icon = iconMap[item.icon]
            return (
              <button
                key={item.href}
                onClick={() => onNavigate?.(item.href)}
                className={`flex flex-col items-center justify-center gap-0.5 flex-1 h-full transition-colors min-w-0
                  ${item.isActive
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300'
                  }`}
              >
                {item.iconUrl ? (
                  <img src={item.iconUrl} alt="" className="w-[18px] h-[18px] object-contain" />
                ) : (
                  <Icon className="w-[18px] h-[18px]" strokeWidth={item.isActive ? 2.5 : 2} />
                )}
                <span className="text-[10px] font-medium leading-tight truncate max-w-full px-0.5">
                  {item.shortLabel || item.label}
                </span>
              </button>
            )
          })}
        </div>
      </nav>
    )
  }

  // Top variant (desktop/tablet)
  return (
    <nav className="flex items-center gap-1 px-4 md:px-6">
      {items.map((item) => {
        const Icon = iconMap[item.icon]
        return (
          <button
            key={item.href}
            onClick={() => onNavigate?.(item.href)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap
              ${item.isActive
                ? 'text-emerald-600 dark:text-emerald-400 border-emerald-600 dark:border-emerald-400'
                : 'text-zinc-600 dark:text-zinc-400 border-transparent hover:text-zinc-900 dark:hover:text-zinc-200 hover:border-zinc-300 dark:hover:border-zinc-600'
              }`}
          >
            {item.iconUrl ? (
              <img src={item.iconUrl} alt="" className="w-4 h-4 object-contain" />
            ) : (
              <Icon className="w-4 h-4" />
            )}
            <span>{item.label}</span>
          </button>
        )
      })}
    </nav>
  )
}
